import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography, Grid, Card, Descriptions, Collapse } from "antd";
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import useMobileFullscreenModalBackHandler from "../../../Hooks/useMobileFullscreenModalBackHandler";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const formatKey = (key) => {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Check if a value appears to be a date string
const isDateString = (value) => {
    if (typeof value !== 'string') return false;

    // Common date formats in ISO or similar with timezone offset
    const datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/;
    return datePattern.test(value) && !isNaN(Date.parse(value));
};

// Format value based on its type
const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';

    if (isDateString(value)) {
        try {
            return new Date(value).toLocaleString();
        } catch (e) {
            return String(value);
        }
    }

    return String(value);
};

const ObjectDetailSection = ({ label, data, showTitle = true }) => {
    const screens = useBreakpoint();
    const isMobile = screens.xs;

    if (!data || typeof data !== 'object') return null;

    const content = (
        <Descriptions
            bordered
            column={1}
            size="small"
            style={{
                marginTop: 5,
                marginBottom: 5,
                width: '100%'
            }}
            labelStyle={{
                padding: isMobile ? '8px' : '8px 16px',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                wordBreak: isMobile ? 'break-word' : 'normal'
            }}
            contentStyle={{
                padding: isMobile ? '8px' : '8px 16px',
                wordBreak: isMobile ? 'break-word' : 'normal'
            }}
        >
            {Object.entries(data).map(([key, value]) => {
                if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                    // For mobile, use a cleaner nested approach
                    if (isMobile) {
                        return (
                            <Descriptions.Item
                                key={key}
                                label={formatKey(key)}
                                labelStyle={{ fontWeight: 'bold' }}
                            >
                                <div style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '4px',
                                    padding: '8px',
                                    backgroundColor: '#fafafa',
                                    marginTop: '4px'
                                }}>
                                    {Object.entries(value).map(([nestedKey, nestedValue]) => {
                                        if (nestedValue !== null && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
                                            // For deeply nested objects, use a simplified approach
                                            return (
                                                <div key={nestedKey} style={{ marginBottom: '8px' }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                        {formatKey(nestedKey)}:
                                                    </div>
                                                    <ObjectDetailSection
                                                        key={nestedKey}
                                                        label={formatKey(nestedKey)}
                                                        data={nestedValue}
                                                        showTitle={false}
                                                    />
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={nestedKey} style={{ marginBottom: '4px' }}>
                                                <span style={{ fontWeight: 'bold' }}>{formatKey(nestedKey)}: </span>
                                                <span>{formatValue(nestedValue)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Descriptions.Item>
                        );
                    }

                    // Desktop view
                    return (
                        <Descriptions.Item
                            key={key}
                            label={formatKey(key)}
                            style={{ padding: 0 }}
                        >
                            <ObjectDetailSection
                                key={key}
                                label={formatKey(key)}
                                data={value}
                                showTitle={false}
                            />
                        </Descriptions.Item>
                    );
                }
                return (
                    <Descriptions.Item key={key} label={formatKey(key)}>
                        {formatValue(value)}
                    </Descriptions.Item>
                );
            })}
        </Descriptions>
    );

    // Only wrap in a Card if showTitle is true
    if (showTitle) {
        return (
            <Card
                title={label}
                size="small"
                style={{
                    marginTop: 10,
                    marginBottom: 10,
                    width: '100%'
                }}
                bordered={true}
                headStyle={{
                    backgroundColor: "#f0f5ff",
                    padding: isMobile ? '8px 12px' : '12px 16px'
                }}
                bodyStyle={{
                    padding: isMobile ? 8 : 16
                }}
            >
                {content}
            </Card>
        );
    }

    return content;
};

const TransactionDetailsModal = ({ visible, onCancel, transactionDetails }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const isMobile = screens.xs;

    // Close fullscreen modal with the mobile system back button
    useMobileFullscreenModalBackHandler(visible && isMobile, onCancel);

    const additionalDetails = useMemo(() => {
        if (!transactionDetails) return [];
        return Object.entries(transactionDetails)
            .filter(([key]) => key !== "user" && key !== "message")
            .map(([key, value]) => ({ key: formatKey(key), value }));
    }, [transactionDetails]);

    const primitiveEntries = useMemo(() => {
        if (!transactionDetails) return [];
        return Object.entries(transactionDetails)
            .filter(([key, value]) => key !== 'user' && key !== 'message' && (value === null || typeof value !== 'object'));
    }, [transactionDetails]);

    const objectEntries = useMemo(() => {
        if (!transactionDetails) return [];
        return Object.entries(transactionDetails)
            .filter(([key, value]) => key !== 'user' && key !== 'message' && value !== null && typeof value === 'object');
    }, [transactionDetails]);

    if (!transactionDetails) {
        return (
            <ResponsiveModal
                title={<Title level={4}>{t('transaction_details')}</Title>}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                desktopWidth={720}
            >
                <Typography.Text>{t('no_transaction_details_available')}</Typography.Text>
            </ResponsiveModal>
        );
    }

    return (
        <ResponsiveModal
            title={<Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>{t('transaction_details')}</Title>}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            desktopWidth={720}
        >
            <Card bordered style={{ marginBottom: 12 }} size={isMobile ? 'small' : 'default'}>
                <Title level={isMobile ? 5 : 4} style={{ marginTop: 0 }}>{t('summary')}</Title>
                <Descriptions column={isMobile ? 1 : 2} size="small">
                    {primitiveEntries.length === 0 && (
                        <Descriptions.Item label={t('info')}>—</Descriptions.Item>
                    )}
                    {primitiveEntries.map(([key, value]) => (
                        <Descriptions.Item key={key} label={formatKey(key)}>
                            {formatValue(value)}
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            </Card>

            {transactionDetails.user && (
                <Card bordered style={{ marginBottom: 12 }} size={isMobile ? 'small' : 'default'}>
                    <Title level={isMobile ? 5 : 5} style={{ marginTop: 0 }}>{t('user_details')}</Title>
                    <Descriptions column={isMobile ? 1 : 2} size="small">
                        <Descriptions.Item label={t('id')}>{formatValue(transactionDetails.user.user_id)}</Descriptions.Item>
                        <Descriptions.Item label={t('full_name')}>{formatValue(transactionDetails.user.full_name)}</Descriptions.Item>
                    </Descriptions>
                </Card>
            )}

            {transactionDetails.message && (
                <Card bordered style={{ marginBottom: 12 }} size={isMobile ? 'small' : 'default'}>
                    <Title level={isMobile ? 5 : 5} style={{ marginTop: 0 }}>{t('message_details')}</Title>
                    {Object.entries(transactionDetails.message).map(([key, value]) => (
                        value !== null && typeof value === 'object' && !Array.isArray(value)
                            ? (
                                <ObjectDetailSection
                                    key={key}
                                    label={formatKey(key)}
                                    data={value}
                                />
                            ) : (
                                <Descriptions key={key} column={isMobile ? 1 : 2} size="small">
                                    <Descriptions.Item label={formatKey(key)}>{formatValue(value)}</Descriptions.Item>
                                </Descriptions>
                            )
                    ))}
                </Card>
            )}

            {objectEntries.length > 0 && (
                <Collapse ghost>
                    <Collapse.Panel header={t('more_details')} key="more">
                        {objectEntries.map(([key, value]) => (
                            <ObjectDetailSection key={key} label={formatKey(key)} data={value} />
                        ))}
                    </Collapse.Panel>
                </Collapse>
            )}

        </ResponsiveModal>
    );
};

TransactionDetailsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    transactionDetails: PropTypes.shape({
        user: PropTypes.shape({
            user_id: PropTypes.string,
            full_name: PropTypes.string,
        }),
        message: PropTypes.object,
    }),
};

export default TransactionDetailsModal;
